import services from "../../axios/dbManag";
import { alertFailed, alertSuccess, alertWarning } from "@/composables/swAlert";
import {
  getSportpayMessage,
  getSportpayStatusCode,
} from "@/requests/economato";

const lowerCaseKeys = (obj) =>
  obj && typeof obj === "object"
    ? Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v])
      )
    : obj;

const isEmptyValue = (v) =>
  v === null || v === undefined || String(v).trim() === "";

const DEFAULT_PAYMENT_ERROR =
  "Non e' stato possibile effettuare il pagamento. Riprovare piu' tardi";

// campi che, se tutti vuoti, indicano che il movimento non è un vero movimento
// ma solo un messaggio informativo (es. "Affiliazione non ancora approvata")
const CAMPI_MOVIMENTO = [
  "id",
  "numero",
  "tipo",
  "causale",
  "importo",
  "data_movimento",
];

const mapMovimentoToCartItem = (row, index) => {
  const sospeso = row.sospeso === 1;
  // pagabile assente => retrocompatibilità: si considera pagabile
  const pagabile =
    row.pagabile === null || row.pagabile === undefined
      ? true
      : Number(row.pagabile) === 1;
  // solo descrizione valorizzata => da mostrare come alert warning
  const isAvviso =
    !pagabile && CAMPI_MOVIMENTO.every((k) => isEmptyValue(row[k]));

  return {
    id: isEmptyValue(row.id) ? `avviso-${index}` : row.id,
    type: row.tipo,
    causale: row.causale,
    name: row.descrizione,
    price: pagabile ? Math.abs(row.importo ?? 0) : 0,
    insertedAt: row.data_movimento,
    required: pagabile && !!row.pagamento_obbligatorio,
    pagabileSingolarmente: row.pagabile_singolarmente === 1,
    pagabile,
    isAvviso,
    sospeso,
    checked: pagabile && !sospeso,
  };
};

export default {
  namespaced: true,

  state: () => ({
    items: [],
    drawerOpen: false,
    isPaying: false,
    isLoading: false,
    creditoDisponibile: 0,
    paymentTick: 0,
  }),

  getters: {
    drawerOpen: (state) => state.drawerOpen,
    isPaying: (state) => state.isPaying,
    isLoading: (state) => state.isLoading,
    creditoDisponibile: (state) => state.creditoDisponibile,
    paymentTick: (state) => state.paymentTick,

    // 🔥 totale SOLO items checked
    subtotal: (state) =>
      Math.round(
        state.items
          .filter((item) => item.checked && item.pagabile)
          .reduce((total, item) => total + item.price, 0) * 100
      ) / 100,

    avvisi: (state) => state.items.filter((i) => i.isAvviso),

    totaleDaPagare: (state, getters, rootState, rootGetters) => {
      if (!rootGetters.conto_sportpay) return getters.subtotal;
      const netto = Math.max(
        getters.subtotal - Math.max(state.creditoDisponibile, 0),
        0
      );
      return Math.round(netto * 100) / 100;
    },

    // gli avvisi non sono movimenti: non vanno conteggiati nel badge del carrello
    totalItems: (state) => state.items.filter((i) => !i.isAvviso).length,

    allSelected: (state) => {
      const selectable = state.items.filter(
        (i) => !i.required && !i.sospeso && i.pagabile
      );
      if (!selectable.length) return false;
      return selectable.every((i) => i.checked);
    },

    requiredLocked: (state) =>
      state.items.some(
        (i) => i.checked && !i.pagabileSingolarmente && !i.required
      ),
  },

  mutations: {
    SET_DRAWER(state, value) {
      state.drawerOpen = value;
    },

    TOGGLE_ITEM(state, id) {
      const item = state.items.find((i) => i.id === id);
      if (!item || item.sospeso || !item.pagabile) return;
      const locked = state.items.some(
        (i) => i.checked && !i.pagabileSingolarmente && !i.required
      );
      if (item.required && locked) return;
      item.checked = !item.checked;
      const nowLocked = state.items.some(
        (i) => i.checked && !i.pagabileSingolarmente && !i.required
      );
      if (nowLocked) {
        state.items.forEach((i) => {
          if (i.required && !i.sospeso) i.checked = true;
        });
      }
    },

    SET_ALL_CHECKED(state, value) {
      state.items.forEach((i) => {
        if (i.sospeso || !i.pagabile) return;
        if (!i.required) i.checked = value;
      });
      const locked = state.items.some(
        (i) => i.checked && !i.pagabileSingolarmente && !i.required
      );
      state.items.forEach((i) => {
        if (!i.required || i.sospeso || !i.pagabile) return;
        i.checked = locked ? true : value;
      });
    },

    CLEAR_CART(state) {
      state.items = [];
    },

    SET_ITEMS(state, items) {
      state.items = items;
    },

    SET_PAYING(state, value) {
      state.isPaying = value;
    },

    SET_LOADING(state, value) {
      state.isLoading = value;
    },

    SET_CREDITO(state, value) {
      state.creditoDisponibile = Number(value) || 0;
    },

    BUMP_PAYMENT_TICK(state) {
      state.paymentTick++;
    },
  },

  actions: {
    async openDrawer({ commit, dispatch }) {
      commit("SET_DRAWER", true);
      await dispatch("loadCartData");
    },

    async loadCartData({ commit, dispatch, rootGetters }) {
      const id_societa = rootGetters.id_societa;
      if (!id_societa) return;
      commit("SET_LOADING", true);
      try {
        await Promise.all([
          dispatch("loadMovimenti", { id_societa }),
          dispatch("loadCredito", { id_societa }),
        ]);
      } finally {
        commit("SET_LOADING", false);
      }
    },

    closeDrawer({ commit }) {
      commit("SET_DRAWER", false);
    },

    toggleItem({ commit }, id) {
      commit("TOGGLE_ITEM", id);
    },
    toggleAll({ commit, getters }) {
      commit("SET_ALL_CHECKED", !getters.allSelected);
    },

    async loadMovimenti({ commit, rootGetters }, { id_societa }) {
      if (!id_societa) return;
      const stagione = rootGetters.stagioneSelected;
      try {
        const res = await services.dbManag.post(
          "/economato/societa/movimenti/list",
          {
            id_societa,
            stagione,
            carrello: 1,
            rowstoskip: 0,
            fetchrows: 10000,
          }
        );
        const results = res?.data?.results || [];
        const items = results.map(mapMovimentoToCartItem);

        commit("SET_ITEMS", items);
      } catch (e) {
        commit("SET_ITEMS", []);
      }
    },

    async loadCredito({ commit, rootGetters }, { id_societa }) {
      if (!id_societa) return;
      const stagione = rootGetters.stagioneSelected;
      try {
        const res = await services.dbManag.post(
          "/economato/societa/movimenti_riepilogo/list",
          { id_societa, stagione }
        );
        const saldo = res?.data?.results?.[0]?.saldo ?? 0;
        commit("SET_CREDITO", saldo);
      } catch (e) {
        commit("SET_CREDITO", 0);
      }
    },

    async inizializzaPagamento({ state, getters, rootGetters }) {
      console.log(getters.totaleDaPagare);
      const checkedItems = state.items.filter((i) => i.checked && i.pagabile);
      const ids_movimenti = checkedItems.map((i) => i.id).join(",");
      const importo = getters.totaleDaPagare;
      /* const importo = "0.10"; per test */
      const descrizione = `Pagamento FITP - ${checkedItems.length} movimenti - € ${importo}`;
      const id_Societa = rootGetters.id_societa;
      const anno = rootGetters.stagioneSelected;
      // {
      //   "arr_Movimenti": "string",
      //   "descrizione": "string",
      //   "importo": 0,
      //   "id_Societa": 0,
      //   "id_Persona": 0,
      //   "anno": 0,
      //   "data_Competenza": "2026-05-26T11:12:11.931Z",
      //   "pagamento_isf": true
      // }
      try {
        const res = await services.dbManag.post(
          "/sportpay/pagamento/inizializza",
          {
            arr_Movimenti: ids_movimenti,
            importo,
            descrizione,
            id_Societa,
            anno,
          }
        );
        if (res?.data?.error) {
          alertFailed(res.data.error);
          return null;
        }
        return lowerCaseKeys(res?.data?.results?.[0]) ?? null;
      } catch (e) {
        alertFailed(
          e?.response?.data?.error ||
            e?.response?.data?.message ||
            "Errore durante l'inizializzazione del pagamento"
        );
        return null;
      }
    },

    async pagaPagamento(_, { guid_transazione, fitpIdentifierCode }) {
      try {
        const res = await services.sportpay.post(
          "/v1/sportpay/pagamento/paga",
          {
            guid_transazione,
            fitpIdentifierCode,
          }
        );
        const statusCode = getSportpayStatusCode(res?.data, res.status);
        const message = getSportpayMessage(res?.data) || DEFAULT_PAYMENT_ERROR;
        if (res?.data?.error || res?.data?.Error || statusCode >= 400) {
          alertFailed(
            message ||
              "Non Ã¨ stato possibile effettuare il pagamento. Riprovare piÃ¹ tardi"
          );
          return null;
        }
        return { status: statusCode, data: res.data };
      } catch (e) {
        const msg =
          getSportpayMessage(e?.response?.data) || DEFAULT_PAYMENT_ERROR;
        alertFailed(
          msg ||
            "Non è stato possibile effettuare il pagamento. Riprovare più tardi"
        );
        return null;
      }
    },

    async checkout({ dispatch, commit, state }) {
      if (state.isPaying) return;
      const hasChecked = state.items.some((i) => i.checked);
      if (!hasChecked) return;
      commit("SET_PAYING", true);
      try {
        const init = await dispatch("inizializzaPagamento");
        if (!init) return;
        if (!init.guid_transazione) {
          alertFailed(
            "Non è stato possibile effettuare il pagamento. Riprovare più tardi"
          );
          return;
        }

        const res = await dispatch("pagaPagamento", {
          guid_transazione: init.guid_transazione,
          fitpIdentifierCode: init.fitpidentifiercode,
        });
        if (!res) return;

        const { status, data } = res;
        const message = getSportpayMessage(data);
        const errorMessage = message || DEFAULT_PAYMENT_ERROR;

        if (status === 200) {
          alertSuccess(message || "Pagamento andato a buon fine");
        } else if (status === 202) {
          alertWarning(message || "Pagamento in sospeso");
        } else {
          alertFailed(
            errorMessage ||
              "Non è stato possibile effettuare il pagamento. Riprovare più tardi"
          );
          return;
        }

        commit("BUMP_PAYMENT_TICK");
        await dispatch("loadCartData");
      } finally {
        commit("SET_PAYING", false);
      }
    },
  },
};
